<template>
  <div class="status-filter-bar">
    <button
      v-for="chip in chips"
      :key="chip.chave"
      type="button"
      class="chip"
      :class="{ 'chip--ativo': value === chip.chave }"
      :style="estiloChip(chip)"
      @click="$emit('input', chip.chave)"
    >
      <span v-if="chip.dot" class="chip__dot" :style="estiloDot(chip)"></span>
      <span class="chip__label">{{ chip.label }}</span>
      <span class="chip__count">{{ contagens[chip.chave] || 0 }}</span>
    </button>
  </div>
</template>

<script>
// Cor padrão do estado ativo quando o chip não define `cor` (navy do tema).
const COR_ATIVO_PADRAO = '#3a3e81';
// Cor padrão do dot quando o chip não define `cor` (cinza neutro).
const COR_DOT_PADRAO = '#949aa8';

/**
 * Barra de chips de filtro de status (seleção única, v-model).
 * Componente único compartilhado pelas telas de Cargas e de SQLs de Integração:
 * cada tela injeta seus próprios chips via prop, com a cor do dot e do estado
 * ativo definida por chip (style binding) em vez de classes por chave.
 */
export default {
  name: 'StatusFilterBar',
  props: {
    // chave do filtro ativo (v-model)
    value: {
      type: String,
      default: 'todas',
    },
    // contagens calculadas sobre a lista completa, keyed por chave de chip
    contagens: {
      type: Object,
      default: () => ({}),
    },
    // chips exibidos: [{ chave, label, dot?, cor?, borda? }]
    //  - chave: identificador do filtro (obrigatório)
    //  - label: texto exibido (obrigatório)
    //  - dot:   exibe o ponto colorido antes do label
    //  - cor:   cor do dot e do fundo/borda no estado ativo
    //  - borda: cor de borda de acento no estado idle (opcional)
    chips: {
      type: Array,
      required: true,
      validator(chips) {
        return (
          Array.isArray(chips) &&
          chips.length > 0 &&
          chips.every(
            (c) =>
              c &&
              typeof c === 'object' &&
              typeof c.chave === 'string' &&
              c.chave.length > 0 &&
              typeof c.label === 'string' &&
              c.label.length > 0
          )
        );
      },
    },
  },
  methods: {
    estiloChip(chip) {
      if (this.value === chip.chave) {
        const cor = chip.cor || COR_ATIVO_PADRAO;
        return { background: cor, borderColor: cor };
      }
      if (chip.borda) {
        return { borderColor: chip.borda };
      }
      return null;
    },
    estiloDot(chip) {
      // No estado ativo o dot fica branco sobre o fundo colorido.
      if (this.value === chip.chave) {
        return { background: '#fff' };
      }
      return { background: chip.cor || COR_DOT_PADRAO };
    },
  },
};
</script>

<style scoped>
.status-filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 14px 0 18px;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #5a6072;
  background: #fff;
  border: 1px solid #d3d8e0;
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s, color 0.15s;
}

.chip:hover {
  border-color: #b9c0cc;
}

.chip__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 5px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  color: #5a6072;
  background: #eef0f3;
  border-radius: 999px;
}

.chip__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

/* Estado ativo: preenchido na cor do chip (via style binding),
   contador em pílula translúcida */
.chip--ativo {
  color: #fff;
}

.chip--ativo .chip__count {
  color: #fff;
  background: rgba(255, 255, 255, 0.25);
}
</style>
